import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Button,
  Table,
  Switch,
  Space,
  message,
  Spin,
  Alert,
  Typography,
  Tag,
  Divider,
  Row,
  Col,
  Form,
  Input,
  Select,
  DatePicker,
  AutoComplete,
  Checkbox,
  Avatar,
  Popconfirm,
  Tooltip,
  Dropdown
} from 'antd';
import {
  MedicineBoxOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
  HolderOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import {
  ORGAN_SYSTEM_COLORS,
  getAllOrganSystems
} from '../utils/organSystemConstants';
import { IconOnlyOrganSystemTags } from './OrganSystemTags';
import moment from 'moment';
import ProblemPlanModal from './ProblemPlanModal';
import { buildAssessmentAndPlanText } from '../utils/problemPlanUtils';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import '../styles/ProblemPlan.css';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const CheckboxGroup = Checkbox.Group;

// Sortable Row Component for drag-and-drop
const SortableRow = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child) => {
        if (child.key === 'drag') {
          return React.cloneElement(child, {
            children: (
              <div
                {...listeners}
                style={{
                  touchAction: 'none',
                  cursor: 'grab',
                }}
              >
                <HolderOutlined />
              </div>
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const ProblemListManagementModal = ({
  visible,
  onCancel,
  patientId,
  onProblemsToggle,
  currentAssessmentText = ''
}) => {
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProblems, setSelectedProblems] = useState(new Set());

  // Add/Edit Problem Modal states
  const [isAddEditModalVisible, setIsAddEditModalVisible] = useState(false);
  const [editingProblem, setEditingProblem] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [form] = Form.useForm();

  // Inline editing states
  const [editingNotes, setEditingNotes] = useState({});
  const [tempNotes, setTempNotes] = useState({});

  // Problem name suggestions
  const [problemNameOptions, setProblemNameOptions] = useState([]);

  // Permission hooks
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [selectedSnomedCode, setSelectedSnomedCode] = useState(null);

  // Plan Modal states
  const [isPlanModalVisible, setIsPlanModalVisible] = useState(false);
  const [selectedProblemForPlan, setSelectedProblemForPlan] = useState(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchProblems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔍 Fetching problems for patient:', patientId);
      console.log('🔍 API URL:', `/patients/${patientId}/problems`);

      const response = await axios.get(`/patients/${patientId}/problems`);

      console.log('✅ Problems API response status:', response.status);
      console.log('✅ Problems API response data:', response.data);
      console.log('✅ Response data type:', typeof response.data);
      console.log('✅ Response data keys:', Object.keys(response.data || {}));

      // Try different possible data structures
      let problemsData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          problemsData = response.data;
          console.log('📋 Data is direct array');
        } else if (response.data.data && response.data.data.problems && Array.isArray(response.data.data.problems)) {
          problemsData = response.data.data.problems;
          console.log('📋 Data is in response.data.data.problems');
        } else if (response.data.data && Array.isArray(response.data.data)) {
          problemsData = response.data.data;
          console.log('📋 Data is in response.data.data');
        } else if (response.data.problems && Array.isArray(response.data.problems)) {
          problemsData = response.data.problems;
          console.log('📋 Data is in response.data.problems');
        } else {
          console.log('📋 Unexpected data structure, using empty array');
          console.log('📋 Available keys:', Object.keys(response.data));
          problemsData = [];
        }
      }

      console.log('📋 Final problems data:', problemsData);
      console.log('📋 Problems count:', problemsData.length);

      // Ensure we have an array
      setProblems(Array.isArray(problemsData) ? problemsData : []);
    } catch (err) {
      console.error('❌ Error fetching problems:', err);
      console.error('❌ Error status:', err.response?.status);
      console.error('❌ Error data:', err.response?.data);
      console.error('❌ Error message:', err.message);
      setError(`Failed to fetch problems: ${err.response?.data?.message || err.message}`);
      message.error('Failed to fetch problems');
      setProblems([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  // Parse current assessment text to identify which problems are already included
  const parseCurrentAssessment = useCallback(() => {
    if (!currentAssessmentText || !Array.isArray(problems)) {
      setSelectedProblems(new Set());
      return;
    }

    const selected = new Set();
    problems.forEach(problem => {
      // Check if problem name appears in the assessment text
      if (problem.problemName && currentAssessmentText.includes(problem.problemName)) {
        selected.add(problem._id);
      }
    });
    setSelectedProblems(selected);
  }, [currentAssessmentText, problems]);

  // Fetch problems when modal opens
  useEffect(() => {
    if (visible && patientId) {
      fetchProblems();
    }
  }, [visible, patientId, fetchProblems]);

  // Parse current assessment when problems or assessment text changes
  useEffect(() => {
    if (Array.isArray(problems) && problems.length > 0) {
      parseCurrentAssessment();
    }
  }, [problems, currentAssessmentText, parseCurrentAssessment]);

  // Handle problem toggle
  const handleProblemToggle = (problemId, checked) => {
    const newSelected = new Set(selectedProblems);
    
    if (checked) {
      newSelected.add(problemId);
    } else {
      newSelected.delete(problemId);
    }
    
    setSelectedProblems(newSelected);
    
    // Build the updated assessment text
    const selectedProblemsList = Array.isArray(problems) ? problems.filter(p => newSelected.has(p._id)) : [];
    const assessmentText = buildAssessmentText(selectedProblemsList);
    
    // Call the callback to update the parent form
    if (onProblemsToggle) {
      onProblemsToggle(assessmentText);
    }
  };

  // Build assessment text from selected problems with plan integration
  const buildAssessmentText = (selectedProblemsList) => {
    return buildAssessmentAndPlanText(selectedProblemsList, 'en');
  };

  // Add/Edit Problem Modal functions
  const showAddEditModal = (problem = null) => {
    setEditingProblem(problem);
    if (problem) {
      // Pre-fill form for editing
      form.setFieldsValue({
        ...problem,
        onsetDate: problem.onsetDate ? moment(problem.onsetDate) : null,
        resolvedDate: problem.resolvedDate ? moment(problem.resolvedDate) : null,
      });
    } else {
      // Reset form for adding
      form.resetFields();
      form.setFieldsValue({
        status: 'Active',
        priority: 'Medium'
      });
    }
    setIsAddEditModalVisible(true);
  };

  const handleAddEditCancel = () => {
    setIsAddEditModalVisible(false);
    setEditingProblem(null);
    form.resetFields();
  };

  const handleAddEditFinish = async (values) => {
    setSubmitLoading(true);
    setError(null);

    const payload = {
      ...values,
      problemName: values.problemName.trim(),
      onsetDate: values.onsetDate ? values.onsetDate.toISOString() : null,
      resolvedDate: values.resolvedDate ? values.resolvedDate.toISOString() : null,
      icdCode: selectedSnomedCode || values.icdCode, // Use SNOMED code if available
    };
    // Ensure patientFriendlyProblemName is included
    if (form.getFieldValue('patientFriendlyProblemName')) {
      payload.patientFriendlyProblemName = form.getFieldValue('patientFriendlyProblemName');
    }
    if (form.getFieldValue('problemName_vn')) {
      payload.problemName_vn = form.getFieldValue('problemName_vn');
    }
    if (form.getFieldValue('patientFriendlyProblemName_vn')) {
      payload.patientFriendlyProblemName_vn = form.getFieldValue('patientFriendlyProblemName_vn');
    }

    try {
      if (editingProblem) {
        // Update existing problem
        await axios.patch(`/problems/${editingProblem._id}`, payload);
        message.success('Problem updated successfully!');
      } else {
        // Create new problem
        await axios.post(`/patients/${patientId}/problems`, payload);
        message.success('Problem added successfully!');
      }
      handleAddEditCancel();
      fetchProblems(); // Refresh list
    } catch (err) {
      console.error("Error saving problem:", err);
      let errorMessage = 'Failed to save problem.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      message.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (problemId) => {
    try {
      await axios.delete(`/problems/${problemId}`);
      message.success('Problem deleted successfully!');
      fetchProblems(); // Refresh list
      // Remove from selected problems if it was selected
      setSelectedProblems(prev => {
        const newSet = new Set(prev);
        newSet.delete(problemId);
        return newSet;
      });
    } catch (err) {
      console.error("Error deleting problem:", err);
      let errorMessage = 'Failed to delete problem.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      message.error(errorMessage);
    }
  };

  // Problem name search function
  const handleProblemSearch = async (searchText) => {
    if (searchText.length > 2) {
      try {
        const response = await axios.get(`/medical-terms/search?term=${searchText}`);
        const suggestions = response.data.data.map(term => ({
          value: term.description,
          key: term.snomedCID,
          id: term._id,
          patientFriendlyName: term.patientFriendlyName,
          description_vn: term.description_vn,
          patientFriendlyName_vn: term.patientFriendlyName_vn,
        }));
        setProblemNameOptions(suggestions);
      } catch (error) {
        console.error('Error fetching problem suggestions:', error);
        setProblemNameOptions([]);
      }
    } else {
      setProblemNameOptions([]);
    }
  };

  const onProblemSelect = async (value, option) => {
    const selectedTerm = problemNameOptions.find(opt => opt.key === option.key);
    setSelectedSnomedCode(option.key);

    // Initial form set
    form.setFieldsValue({
      problemName: value,
      icdCode: option.key,
      patientFriendlyProblemName: selectedTerm?.patientFriendlyName || value,
    });

    // Check if translation is needed
    if (!selectedTerm?.description_vn || !selectedTerm?.patientFriendlyName_vn) {
      try {
        message.loading({ content: 'Translating term...', key: 'translating' });
        const response = await axios.post('/medical-terms/translate-and-save', { termId: selectedTerm.id });
        const translatedTerm = response.data.data;
        
        form.setFieldsValue({
          problemName_vn: translatedTerm.description_vn,
          patientFriendlyProblemName_vn: translatedTerm.patientFriendlyName_vn,
        });
        message.success({ content: 'Translation complete!', key: 'translating', duration: 2 });
      } catch (error) {
        console.error('Error translating term:', error);
        message.error({ content: 'Translation failed.', key: 'translating', duration: 2 });
      }
    } else {
      form.setFieldsValue({
        problemName_vn: selectedTerm.description_vn,
        patientFriendlyProblemName_vn: selectedTerm.patientFriendlyName_vn,
      });
    }
  };

  // Inline notes editing functions
  const startEditingNotes = (problemId, currentNotes) => {
    setEditingNotes(prev => ({ ...prev, [problemId]: true }));
    setTempNotes(prev => ({ ...prev, [problemId]: currentNotes || '' }));
  };

  const cancelEditingNotes = (problemId) => {
    setEditingNotes(prev => {
      const newState = { ...prev };
      delete newState[problemId];
      return newState;
    });
    setTempNotes(prev => {
      const newState = { ...prev };
      delete newState[problemId];
      return newState;
    });
  };

  const saveNotes = async (problemId) => {
    try {
      const newNotes = tempNotes[problemId] || '';
      await axios.patch(`/problems/${problemId}`, { notes: newNotes });
      message.success('Notes updated successfully!');

      // Update local state
      setProblems(prev => prev.map(problem =>
        problem._id === problemId ? { ...problem, notes: newNotes } : problem
      ));

      cancelEditingNotes(problemId);

      // Update assessment text if this problem is selected
      if (selectedProblems.has(problemId)) {
        const updatedProblems = problems.map(problem =>
          problem._id === problemId ? { ...problem, notes: newNotes } : problem
        );
        const selectedProblemsList = updatedProblems.filter(p => selectedProblems.has(p._id));
        const assessmentText = buildAssessmentText(selectedProblemsList);
        if (onProblemsToggle) {
          onProblemsToggle(assessmentText);
        }
      }
    } catch (err) {
      console.error("Error updating notes:", err);
      message.error('Failed to update notes');
    }
  };

  // Handle drag end for reordering problems
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = problems.findIndex((item) => item._id === active.id);
      const newIndex = problems.findIndex((item) => item._id === over.id);

      const newProblems = arrayMove(problems, oldIndex, newIndex);

      // Update local state immediately for better UX
      setProblems(newProblems);

      try {
        // Create the sort order array
        const problemOrders = newProblems.map((problem, index) => ({
          problemId: problem._id,
          sortOrder: index
        }));

        // Send to backend
        await axios.put(`/patients/${patientId}/problems/sort-order`, {
          problemOrders
        });

        message.success('Problem order updated successfully!');
      } catch (err) {
        console.error('Error updating problem order:', err);
        message.error('Failed to update problem order');
        // Revert local state on error
        fetchProblems();
      }
    }
  };

  // Handle status update
  const handleStatusUpdate = async (problemId, newStatus) => {
    try {
      await axios.patch(`/problems/${problemId}`, {
        status: newStatus
      });

      // Update local state
      setProblems(prevProblems =>
        prevProblems.map(problem =>
          problem._id === problemId
            ? { ...problem, status: newStatus }
            : problem
        )
      );

      message.success('Problem status updated successfully!');
    } catch (err) {
      console.error('Error updating problem status:', err);
      message.error('Failed to update problem status');
    }
  };

  // Handle priority update
  const handlePriorityUpdate = async (problemId, newPriority) => {
    try {
      await axios.patch(`/problems/${problemId}`, {
        priority: newPriority
      });

      // Update local state
      setProblems(prevProblems =>
        prevProblems.map(problem =>
          problem._id === problemId
            ? { ...problem, priority: newPriority }
            : problem
        )
      );

      message.success('Problem priority updated successfully!');
    } catch (err) {
      console.error('Error updating problem priority:', err);
      message.error('Failed to update problem priority');
    }
  };

  // Handle plan modal
  const showPlanModal = (problem) => {
    setSelectedProblemForPlan(problem);
    setIsPlanModalVisible(true);
  };

  const handlePlanModalCancel = () => {
    setIsPlanModalVisible(false);
    setSelectedProblemForPlan(null);
  };

  const handlePlanUpdated = (updatedProblem) => {
    // Update the problem in the local state
    setProblems(prevProblems =>
      prevProblems.map(problem =>
        problem._id === updatedProblem._id ? updatedProblem : problem
      )
    );
  };

  // Table columns
  const columns = [
    {
      title: '',
      key: 'drag',
      width: 40,
      render: () => <HolderOutlined style={{ cursor: 'grab', color: '#999' }} />,
    },
    {
      title: 'Toggle',
      key: 'toggle',
      width: 80,
      render: (_, record) => (
        <Switch
          checked={selectedProblems.has(record._id)}
          onChange={(checked) => handleProblemToggle(record._id, checked)}
          size="small"
        />
      ),
    },
    {
      title: 'Problem',
      dataIndex: 'problemName',
      key: 'problemName',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {record.icdCode && (
            <div>
              <Tag color="blue" size="small">{record.icdCode}</Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Organ Systems',
      dataIndex: 'organSystems',
      key: 'organSystems',
      width: 150,
      render: (systems) => (
        <IconOnlyOrganSystemTags
          organSystems={systems}
          maxDisplay={8}
          showMore={false}
          size="small"
          maxRows={2}
          iconsPerRow={4}
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const statusOptions = [
          { key: 'Active', label: 'Active', color: 'green' },
          { key: 'Resolved', label: 'Resolved', color: 'blue' },
          { key: 'Inactive', label: 'Inactive', color: 'orange' }
        ];

        const items = statusOptions.map(option => ({
          key: option.key,
          label: (
            <span onClick={() => handleStatusUpdate(record._id, option.key)}>
              <Tag color={option.color} style={{ margin: 0 }}>
                {option.label}
              </Tag>
            </span>
          ),
        }));

        return (
          <Dropdown
            menu={{ items }}
            trigger={['click']}
            placement="bottomLeft"
          >
            <Tag
              color={status === 'Active' ? 'green' : status === 'Resolved' ? 'blue' : 'orange'}
              style={{ cursor: 'pointer' }}
            >
              {status}
            </Tag>
          </Dropdown>
        );
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority, record) => {
        const priorityOptions = [
          { key: 'Critical', label: 'Critical', color: 'red' },
          { key: 'High', label: 'High', color: 'orange' },
          { key: 'Medium', label: 'Medium', color: 'blue' },
          { key: 'Low', label: 'Low', color: 'green' }
        ];

        const items = priorityOptions.map(option => ({
          key: option.key,
          label: (
            <span onClick={() => handlePriorityUpdate(record._id, option.key)}>
              <Tag color={option.color} style={{ margin: 0 }}>
                {option.label}
              </Tag>
            </span>
          ),
        }));

        return (
          <Dropdown
            menu={{ items }}
            trigger={['click']}
            placement="bottomLeft"
          >
            <Tag
              color={
                priority === 'Critical' ? 'red' :
                priority === 'High' ? 'orange' :
                priority === 'Medium' ? 'blue' :
                priority === 'Low' ? 'green' : 'default'
              }
              style={{ cursor: 'pointer' }}
            >
              {priority}
            </Tag>
          </Dropdown>
        );
      },
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: 200,
      render: (notes, record) => {
        const isEditing = editingNotes[record._id];

        if (isEditing) {
          return (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <Input.TextArea
                value={tempNotes[record._id] || ''}
                onChange={(e) => setTempNotes(prev => ({ ...prev, [record._id]: e.target.value }))}
                rows={2}
                style={{ flex: 1 }}
                placeholder="Enter notes..."
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Tooltip title="Save">
                  <Button
                    type="primary"
                    size="small"
                    icon={<SaveOutlined />}
                    onClick={() => saveNotes(record._id)}
                  />
                </Tooltip>
                <Tooltip title="Cancel">
                  <Button
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => cancelEditingNotes(record._id)}
                  />
                </Tooltip>
              </div>
            </div>
          );
        }

        return (
          <div
            style={{ cursor: 'pointer', minHeight: '20px' }}
            onClick={() => startEditingNotes(record._id, notes)}
          >
            {notes ? (
              <Text type="secondary">{notes}</Text>
            ) : (
              <Text type="secondary" style={{ fontStyle: 'italic' }}>Click to add notes...</Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          {canModify && (
            <Tooltip title="Edit Problem">
              <Button
                icon={<EditOutlined />}
                onClick={() => showAddEditModal(record)}
                size="small"
              />
            </Tooltip>
          )}
          {canModify && (
            <Tooltip title="Manage Plan">
              <Button
                icon={<FileTextOutlined />}
                onClick={() => showPlanModal(record)}
                size="small"
                type="default"
                className="plan-button"
              />
            </Tooltip>
          )}
          {canModify && (
            <Popconfirm
              title="Are you sure you want to delete this problem?"
              onConfirm={() => handleDelete(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete Problem">
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  size="small"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Check if user has permission to manage problems
  const canModify = hasPermission('clinical.problems.manage');

  return (
    <Modal
      title={
        <Space>
          <MedicineBoxOutlined />
          Manage Problem List
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="close" onClick={onCancel}>
          Close
        </Button>
      ]}
      destroyOnClose
    >
      <div>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Text type="secondary">
              Toggle problems to include them in the Assessment & Plan field
            </Text>
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Space>
              {canModify && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => showAddEditModal(null)}
                  size="small"
                >
                  Add Problem
                </Button>
              )}
              <Text type="secondary">
                Selected: {selectedProblems.size} of {Array.isArray(problems) ? problems.length : 0} problems
              </Text>
            </Space>
          </Col>
        </Row>

        {error && (
          <Alert 
            message={error} 
            type="error" 
            showIcon 
            style={{ marginBottom: 16 }} 
          />
        )}

        <Spin spinning={loading}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={Array.isArray(problems) ? problems.map(p => p._id) : []}
              strategy={verticalListSortingStrategy}
            >
              <Table
                columns={columns}
                dataSource={Array.isArray(problems) ? problems : []}
                rowKey="_id"
                pagination={false} // Remove pagination to enable full list drag-and-drop reordering
                size="small"
                scroll={{ x: 800, y: 400 }} // Enable horizontal scroll and vertical scroll for long lists in modal
                components={{
                  body: {
                    row: SortableRow,
                  },
                }}
              />
            </SortableContext>
          </DndContext>
        </Spin>

        {selectedProblems.size > 0 && (
          <>
            <Divider />
            <div>
              <Title level={5}>Preview of Assessment Text:</Title>
              <div 
                style={{ 
                  border: '1px solid #d9d9d9', 
                  padding: 12, 
                  borderRadius: 6,
                  backgroundColor: '#fafafa',
                  maxHeight: 200,
                  overflowY: 'auto'
                }}
                dangerouslySetInnerHTML={{
                  __html: buildAssessmentText(Array.isArray(problems) ? problems.filter(p => selectedProblems.has(p._id)) : [])
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Problem Modal */}
      <Modal
        title={editingProblem ? 'Edit Problem' : 'Add New Problem'}
        open={isAddEditModalVisible}
        onCancel={handleAddEditCancel}
        footer={null}
        destroyOnClose
        width={800}
      >
        <Spin spinning={submitLoading}>
          {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
          <Form form={form} layout="vertical" onFinish={handleAddEditFinish}>
            <Form.Item
              name="problemName"
              label="Problem Name"
              rules={[
                { required: true, message: 'Please enter problem name' },
                { min: 2, message: 'Problem name must be at least 2 characters long' },
                { max: 200, message: 'Problem name cannot exceed 200 characters' },
                { whitespace: true, message: 'Problem name cannot be empty or only whitespace' }
              ]}
            >
              <AutoComplete
                options={problemNameOptions}
                onSearch={handleProblemSearch}
                onSelect={onProblemSelect}
                placeholder="Enter problem name"
              />
            </Form.Item>

            <Form.Item
              name="organSystems"
              label="Organ Systems"
              rules={[
                { required: true, message: 'Please select at least one organ system' },
                { type: 'array', min: 1, message: 'Please select at least one organ system' }
              ]}
            >
              <CheckboxGroup style={{ width: '100%' }}>
                <Row gutter={[8, 8]}>
                  {getAllOrganSystems().map(system => (
                    <Col xs={8} sm={6} md={4} lg={3} key={system.key}>
                      <Checkbox
                        value={system.key}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #d9d9d9',
                          borderRadius: '6px',
                          margin: '2px 0',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                          minHeight: '60px',
                          justifyContent: 'center',
                          fontSize: '11px'
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <Avatar
                            src={system.iconPath}
                            size={24}
                            style={{
                              backgroundColor: ORGAN_SYSTEM_COLORS[system.key] || '#f0f0f0',
                              color: '#fff',
                              width: '24px',
                              height: '24px',
                              minWidth: '24px'
                            }}
                          >
                            {system.name.charAt(0)}
                          </Avatar>
                          <Text style={{ fontSize: '10px', textAlign: 'center', lineHeight: '12px' }}>
                            {system.name}
                          </Text>
                        </div>
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </CheckboxGroup>
            </Form.Item>

            <Form.Item
              name="icdCode"
              label="ICD Code"
              rules={[
                { max: 20, message: 'ICD code cannot exceed 20 characters' },
                { pattern: /^[A-Z0-9.-]*$/, message: 'ICD code can only contain uppercase letters, numbers, dots, and hyphens' }
              ]}
            >
              <Input placeholder="e.g., M79.3" maxLength={20} style={{ textTransform: 'uppercase' }} />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                  <Select>
                    <Option value="Active">Active</Option>
                    <Option value="Resolved">Resolved</Option>
                    <Option value="Inactive">Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="priority" label="Priority" rules={[{ required: true }]}>
                  <Select>
                    <Option value="Critical">Critical</Option>
                    <Option value="High">High</Option>
                    <Option value="Medium">Medium</Option>
                    <Option value="Low">Low</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item name="onsetDate" label="Onset Date">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            {/* Show Resolved Date only if status is Resolved */}
            {form.getFieldValue('status') === 'Resolved' && (
              <Form.Item
                name="resolvedDate"
                label="Resolved Date"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();

                      const onsetDate = form.getFieldValue('onsetDate');
                      if (onsetDate && value.isBefore(onsetDate)) {
                        return Promise.reject(new Error('Resolved date cannot be before onset date'));
                      }

                      if (value.isAfter(moment())) {
                        return Promise.reject(new Error('Resolved date cannot be in the future'));
                      }

                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current.isAfter(moment().endOf('day'))}
                />
              </Form.Item>
            )}

            <Form.Item
              name="notes"
              label="Notes"
              rules={[
                { max: 1000, message: 'Notes cannot exceed 1000 characters' }
              ]}
            >
              <TextArea
                rows={3}
                placeholder="Additional notes about the problem..."
                maxLength={1000}
                showCount
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                {editingProblem ? 'Save Changes' : 'Add Problem'}
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={handleAddEditCancel}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Problem Plan Modal */}
      <ProblemPlanModal
        visible={isPlanModalVisible}
        onCancel={handlePlanModalCancel}
        problem={selectedProblemForPlan}
        onPlanUpdated={handlePlanUpdated}
      />
    </Modal>
  );
};

export default ProblemListManagementModal;
